import { useCallback, useMemo } from 'react';
import type { TableColumnType } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom-v5-compat';

import { config } from '@/config/config';
import { VaultRole, type PersonalVaultAdministration, type Vault } from '@/modules/api/codegen';
import { StaticModal, useConfirmationModal, useModal } from '@/modules/modals';
import { Link, Text } from '@/modules/ui';

import { useDeletePersonalVault } from '../../../hooks/useDeletePersonalVault';
import { useDeleteVault } from '../../../hooks/useDeleteVault';
import type { VaultFormModalData, VaultsTableRow } from '../../../types';
import { isPersonalVaultDeletable, isPersonalVaultRow, personalVaultOwnerLabel } from '../../../utils';
import { VaultMembersCell } from '../../VaultMembersCell';
import { VaultRowActionButton } from '../../VaultRowActionButton';
import * as felaRules from '../VaultsTable.rules';

export const useVaultsTableColumns = () => {
    const { css } = useFela();
    const { formatMessage } = useIntl();
    const { openConfirmationModal } = useConfirmationModal();

    // Dispatching to the registry rather than importing the form on purpose: the form reaches the
    // record-sharing pickers, which cannot be evaluated while the layout initialises — see `settings.ts`.
    // `VaultsSettings` mounts the modal that answers this.
    const { updateModal: openVaultForm } = useModal<VaultFormModalData>(StaticModal.VAULT_FORM);

    // `mutateAsync` is stable, unlike the mutation result object, so the columns memo below survives a
    // re-render.
    const { mutateAsync: deleteVault } = useDeleteVault();
    const { mutateAsync: deletePersonalVault } = useDeletePersonalVault();

    const confirmPersonalDelete = useCallback(
        (vault: PersonalVaultAdministration) => {
            openConfirmationModal({
                title: (
                    <FormattedMessage
                        id='vaults.table.actions.delete.personal.confirmation.title'
                        values={{ owner: personalVaultOwnerLabel(vault) }}
                    />
                ),
                content: <FormattedMessage id='vaults.table.actions.delete.confirmation.content' />,
                onConfirm: async () => {
                    await deletePersonalVault(vault);
                },
                confirmButtonText: formatMessage({ id: 'vaults.table.actions.delete.confirmation.confirm' }),
                cancelButtonText: formatMessage({ id: 'vaults.table.actions.delete.confirmation.cancel' }),
            });
        },
        [openConfirmationModal, formatMessage, deletePersonalVault],
    );

    const confirmDelete = useCallback(
        (vault: Vault) => {
            openConfirmationModal({
                title: <FormattedMessage id='vaults.table.actions.delete.confirmation.title' values={{ vault: vault.name }} />,
                content: <FormattedMessage id='vaults.table.actions.delete.confirmation.content' />,
                onConfirm: async () => {
                    await deleteVault(vault);
                },
                confirmButtonText: formatMessage({ id: 'vaults.table.actions.delete.confirmation.confirm' }),
                cancelButtonText: formatMessage({ id: 'vaults.table.actions.delete.confirmation.cancel' }),
            });
        },
        [openConfirmationModal, formatMessage, deleteVault],
    );

    return useMemo(
        () =>
            (
                [
                    {
                        dataIndex: 'name',
                        sorter: true,
                        width: '18.75rem',
                        // Same target as the sidebar entry: the records list scoped to this vault. Another
                        // user's personal vault cannot be opened, so its name stays plain.
                        render: (value, record) =>
                            isPersonalVaultRow(record) ? (
                                <Text elementType='span' variant='ParagraphSmallMedium'>
                                    {value}
                                </Text>
                            ) : (
                                <Link
                                    type='router'
                                    to={generatePath(config.routes.vault, { vaultId: record.id })}
                                    color='foregroundsPrimary'
                                    customStyle={felaRules.name}
                                >
                                    {value}
                                </Link>
                            ),
                    },
                    {
                        dataIndex: 'secretsCount',
                        width: '9.375rem',
                        render: value => (
                            <Text elementType='span' variant='ParagraphSmallMedium'>
                                {value}
                            </Text>
                        ),
                    },
                    {
                        dataIndex: 'groups',
                        width: '8.75rem',
                        render: (_, record) => (isPersonalVaultRow(record) ? null : <VaultMembersCell members={record.groups} />),
                    },
                    {
                        dataIndex: 'users',
                        // Explicit, so the trailing space is split with the actions column as drawn
                        // instead of being absorbed entirely by this one.
                        width: '14.5rem',
                        // A personal vault's only member is its owner, which is what the admin is shown.
                        render: (_, record) => <VaultMembersCell members={isPersonalVaultRow(record) ? [record.owner] : record.users} />,
                    },
                    {
                        key: 'actions',
                        width: '14.5rem',
                        // Another user's personal vault offers nothing while its owner is around, and only
                        // a delete once the owner is offboarded. Every other row is a vault the caller
                        // belongs to, so a viewer row is ordinary rather than an edge case. An absent role
                        // is not proof of the rights these actions need.
                        render: (_, record) => {
                            if (isPersonalVaultRow(record)) {
                                return isPersonalVaultDeletable(record) ? (
                                    <div className={css(felaRules.actionsCell)}>
                                        <VaultRowActionButton
                                            label='vaults.table.actions.delete'
                                            icon='trash'
                                            color='inputsError'
                                            testId='delete-vault-button'
                                            onClick={() => confirmPersonalDelete(record)}
                                        />
                                    </div>
                                ) : null;
                            }

                            return record.userRole === VaultRole.editor ? (
                                <div className={css(felaRules.actionsCell)}>
                                    <VaultRowActionButton
                                        label='vaults.table.actions.edit'
                                        icon='edit'
                                        color='accentPrimary'
                                        testId='edit-vault-button'
                                        onClick={() => {
                                            openVaultForm(true, { vault: record });
                                        }}
                                    />
                                    <VaultRowActionButton
                                        label='vaults.table.actions.delete'
                                        icon='trash'
                                        color='inputsError'
                                        testId='delete-vault-button'
                                        onClick={() => confirmDelete(record)}
                                    />
                                </div>
                            ) : null;
                        },
                    },
                ] as const satisfies TableColumnType<VaultsTableRow>[]
            ).map(column => ({
                ...column,
                title: 'dataIndex' in column ? formatMessage({ id: `vaults.table.columns.${column.dataIndex}` }) : undefined,
            })),
        [css, formatMessage, confirmDelete, confirmPersonalDelete, openVaultForm],
    );
};
